import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Phone, MessageSquare } from 'lucide-react';
import usePolling from '../../hooks/usePolling';
import apiClient from '../../services/apiClient';
const isAdminRole = (role) => role === 'manager' || role === 'admin' || role === 'owner';
const relativeTime = (iso) => {
  try {
    const then = new Date(iso).getTime();
    if (!then) return '';
    const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
    const days = Math.round(hrs / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  } catch (_) { return ''; }
};
const callWhen = (req) => {
  try {
    const parts = String(req.day || '').split('-').map(Number);
    if (parts.length !== 3) return req.day || '';
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    return `${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short' })} ${d.getDate()}`;
  } catch (_) { return req.day || ''; }
};
const CARD_STYLE = {
  background: 'linear-gradient(145deg, rgba(23,29,48,0.94), rgba(11,15,26,0.96))',
  border: '1px solid rgba(212,175,55,0.35)',
  boxShadow: '0 18px 46px -18px rgba(0,0,0,0.85), 0 0 26px -12px rgba(212,175,55,0.3)',
};
const GOLD_ICON = { background: 'linear-gradient(135deg, #D4AF37, #C5A028)' };
const PRIMARY_BTN = { background: 'linear-gradient(135deg, #D4AF37 0%, #C5A028 100%)', color: '#0B0F1A' };
const GHOST_BTN = { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' };
const LeadCard = ({ n, onOpen, onDismiss }) => {
  const meta = n.metadata || {};
  const name = meta.clientName || 'New lead';
  const type = meta.eventType || '';
  const phone = (meta.phone || '').trim();
  return (
    <div className="rounded-2xl p-4 backdrop-blur-xl animate-in fade-in slide-in-from-bottom-2 duration-300" style={CARD_STYLE} data-testid="attn-lead-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-[#0B0F1A] text-[17px] font-bold" style={GOLD_ICON}>✦</div>
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] font-semibold uppercase tracking-[1px] text-[#E5C76B]">New lead</p>
          <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[1px]" style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.4)', color: '#6EE7B7' }}>{relativeTime(n.createdAt) || 'New'}</span>
          <p className="text-[14px] font-semibold text-white truncate mt-1">{name}</p>
          {type ? <p className="text-[12.5px] text-white/55 mt-0.5">{type}</p> : null}
          {phone ? <a href={`tel:${phone}`} className="text-[12.5px] text-[#E5C76B] hover:underline" data-testid="attn-lead-tel">{phone}</a> : null}
        </div>
        <button type="button" onClick={() => onDismiss(n.id || n._id)} className="p-1.5 rounded-full text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors flex-shrink-0" title="Dismiss" data-testid="attn-lead-dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex gap-2 mt-3.5">
        <button type="button" onClick={() => onOpen(n)} className="flex-1 py-2.5 rounded-xl text-[12.5px] font-semibold transition-all duration-300" style={PRIMARY_BTN} data-testid="attn-lead-open">Open lead</button>
        {phone ? (
          <a href={`sms:${phone}`} className="flex-1 py-2.5 rounded-xl text-[12.5px] font-semibold text-white/70 text-center transition-all duration-300 inline-flex items-center justify-center gap-1.5" style={GHOST_BTN} data-testid="attn-lead-message">
            <MessageSquare className="w-3.5 h-3.5" /> Message
          </a>
        ) : null}
      </div>
    </div>
  );
};
const CallCard = ({ req, onConfirm, onDismiss, onOpen }) => (
  <div className="rounded-2xl p-4 backdrop-blur-xl animate-in fade-in slide-in-from-bottom-2 duration-300" style={CARD_STYLE} data-testid="attn-call-card">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={GOLD_ICON}>
        <Phone className="w-4 h-4 text-[#0B0F1A]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10.5px] font-semibold uppercase tracking-[1px] text-[#E5C76B]">Call booked</p>
        <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[1px]" style={{ background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.45)', color: '#F59E0B' }}>Awaiting your confirm</span>
        <p className="text-[14px] font-semibold text-white truncate mt-1">{req.clientName || 'New lead'}</p>
        <p className="text-[12.5px] text-white/55 mt-0.5">{callWhen(req)} at {req.time}</p>
        {req.phone ? <a href={`tel:${req.phone}`} className="text-[12.5px] text-[#E5C76B] hover:underline" data-testid="attn-call-tel">{req.phone}</a> : null}
      </div>
      <button type="button" onClick={() => onDismiss(req.id)} className="p-1.5 rounded-full text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors flex-shrink-0" title="Dismiss" data-testid="attn-call-dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
    <div className="flex gap-2 mt-3.5">
      <button type="button" onClick={() => onConfirm(req.id)} className="flex-1 py-2.5 rounded-xl text-[12.5px] font-semibold transition-all duration-300" style={PRIMARY_BTN} data-testid="attn-call-confirm">Confirm call</button>
      <button type="button" onClick={() => { if (req.eventId) onOpen(req.eventId); }} className="flex-1 py-2.5 rounded-xl text-[12.5px] font-semibold text-white/70 transition-all duration-300" style={GHOST_BTN} data-testid="attn-call-open">Open enquiry</button>
    </div>
  </div>
);
export default function NeedsAttentionStack({ user }) {
  const navigate = useNavigate();
  const role = user?.role || '';
  const isAdmin = isAdminRole(role);
  const [calls, setCalls] = useState([]);
  const [leads, setLeads] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const loadCalls = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await apiClient.get('/leads/call-requests/pending');
      setCalls(Array.isArray(data?.requests) ? data.requests : []);
    } catch (_) { /* a failed poll must never break the shell */ }
  }, [isAdmin]);
  const loadLeads = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await apiClient.get('/notifications', { params: { limit: 50 } });
      const items = Array.isArray(data) ? data : (data?.notifications || []);
      const open = items.filter((n) => (n.type === 'new_lead')
        && ((n.relatedEntityType || '') !== 'live_lead')
        && !n.read);
      setLeads(open);
    } catch (_) { /* silent */ }
  }, [isAdmin]);
  usePolling(loadCalls, 30000, { enabled: isAdmin, key: 'attn/calls' });
  usePolling(loadLeads, 30000, { enabled: isAdmin, key: 'attn/leads' });
  const dismissCall = useCallback(async (id) => {
    setCalls((prev) => prev.filter((r) => r.id !== id));
    try { await apiClient.post(`/leads/call-requests/${id}/ack`); } catch (_) { /* returns on next poll */ }
  }, []);
  const confirmCall = useCallback(async (id) => {
    setCalls((prev) => prev.filter((r) => r.id !== id));
    try { await apiClient.post(`/leads/call-requests/${id}/confirm`); } catch (_) { /* returns on next poll */ }
  }, []);
  const dismissLead = useCallback(async (id) => {
    if (!id) return;
    setLeads((prev) => prev.filter((n) => (n.id || n._id) !== id));
    try { await apiClient.post(`/notifications/${id}/read`); } catch (_) { /* returns on next poll */ }
  }, []);
  const openLead = useCallback((n) => {
    const eid = n.relatedEntityId || String(n.link || '').split('/event/')[1];
    dismissLead(n.id || n._id);
    if (eid) navigate(`/event/${eid}`);
  }, [navigate, dismissLead]);
  const openEvent = useCallback((eid) => { if (eid) navigate(`/event/${eid}`); }, [navigate]);
  const items = useMemo(() => {
    const L = leads.map((n) => ({ kind: 'lead', key: 'lead-' + (n.id || n._id), raw: n }));
    const C = calls.map((r) => ({ kind: 'call', key: 'call-' + r.id, raw: r }));
    return [...L, ...C];
  }, [leads, calls]);
  const dismissAll = useCallback(() => {
    leads.forEach((n) => dismissLead(n.id || n._id));
    calls.forEach((r) => dismissCall(r.id));
  }, [leads, calls, dismissLead, dismissCall]);
  if (!isAdmin || items.length === 0) return null;
  const shown = expanded ? items : items.slice(0, 2);
  const hidden = items.length - shown.length;
  return (
    <div className="fixed z-[70] bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-[380px] flex flex-col gap-2.5 items-stretch" data-testid="needs-attention-stack">
      <div className="flex items-center justify-between px-1 pb-0.5">
        <span className="flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-[1.2px] text-white/45">
          <span className="w-[7px] h-[7px] rounded-full flex-shrink-0" style={{ background: '#F59E0B', boxShadow: '0 0 8px #F59E0B', animation: 'attnPulse 1.6s ease-in-out infinite' }} />
          Needs your attention · {items.length}
        </span>
        <button type="button" onClick={dismissAll} className="text-[11px] text-white/45 hover:text-white/80 transition-colors" data-testid="needs-attention-dismiss-all">Dismiss all</button>
      </div>
      {shown.map((it) => it.kind === 'lead'
        ? <LeadCard key={it.key} n={it.raw} onOpen={openLead} onDismiss={dismissLead} />
        : <CallCard key={it.key} req={it.raw} onConfirm={confirmCall} onDismiss={dismissCall} onOpen={openEvent} />)}
      {hidden > 0 ? (
        <button type="button" onClick={() => setExpanded(true)} className="text-center text-[11.5px] text-white/45 hover:text-white/75 py-1.5 transition-colors" data-testid="needs-attention-more">+{hidden} more — show all</button>
      ) : null}
      <style>{`@keyframes attnPulse{0%,100%{opacity:1}50%{opacity:.35}}`}</style>
    </div>
  );
}
